import { useEffect } from "react";
import { CONTACT_METHODS, CONTACT_SOCIAL } from "../data/contact";
import PageMeta from "../components/seo/PageMeta";
import { buildContactPageJsonLd, buildWebPageJsonLd } from "../lib/seo";
import "../styles/contact.css";

function EnvelopeIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m3 6 9 7 9-7" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
    </svg>
  );
}

function SocialIconInstagram() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="20" height="20" x="2" y="2" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function SocialIconYouTube() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 17a24.1 24.1 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.6 49.6 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.1 24.1 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.6 49.6 0 0 1-16.2 0A2 2 0 0 1 2.5 17z" />
      <polygon points="10,15 15,12 10,9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function SocialIconFacebook() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function SocialIconLinkedIn() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

const METHOD_ICONS = {
  email: EnvelopeIcon,
  whatsapp: WhatsAppIcon,
};

const SOCIAL_ICONS = {
  instagram: SocialIconInstagram,
  youtube: SocialIconYouTube,
  facebook: SocialIconFacebook,
  linkedin: SocialIconLinkedIn,
};

export default function ContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="contact-page" aria-label="Contact">
      <PageMeta
        title="Contact"
        description="Get in touch with CJ Jayamaha for photography commissions, print orders, collaborations, licensing enquiries, and speaking requests."
        path="/contact"
        keywords={["contact", "commissions", "print orders", "collaborations", "CJ Jayamaha"]}
        jsonLd={[buildContactPageJsonLd(), buildWebPageJsonLd({
          title: "Contact CJ Jayamaha",
          description:
            "Email or WhatsApp CJ Jayamaha for commissions, prints, collaborations, and licensing.",
          path: "/contact",
        })]}
      />
      <header className="contact-page__header">
        <div className="contact-page__eyebrow">CONTACT</div>
        <h1 className="contact-page__title">Let&rsquo;s talk.</h1>
        <p className="contact-page__intro">
          Whether it&rsquo;s a commission, a print, a collaboration, or simply a
          shared love of the road — I&rsquo;d be glad to hear from you. Reach out
          directly and I&rsquo;ll get back to you soon.
        </p>
      </header>

      <div className="contact-page__methods">
        <div className="contact-page__methods-grid">
          {CONTACT_METHODS.map((method) => {
            const Icon = METHOD_ICONS[method.key];
            return (
              <a
                key={method.key}
                href={method.href}
                className="contact-card"
                {...(method.external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                <div className="contact-card__top">
                  <span className="contact-card__icon">
                    <Icon />
                  </span>
                  <span className="contact-card__arrow" aria-hidden="true">
                    →
                  </span>
                </div>
                <div>
                  <div className="contact-card__label">{method.label}</div>
                  <div className="contact-card__value">{method.value}</div>
                  <div className="contact-card__sub">{method.sub}</div>
                </div>
              </a>
            );
          })}
        </div>
      </div>

      <section className="contact-page__social" aria-label="Social links">
        <div className="contact-page__social-inner">
          <div className="contact-page__social-eyebrow">ELSEWHERE</div>
          <h2 className="contact-page__social-title">Follow the journey.</h2>
          <div className="contact-page__social-pills">
            {CONTACT_SOCIAL.map((social) => {
              const Icon = SOCIAL_ICONS[social.key];
              return (
                <a
                  key={social.key}
                  href={social.href}
                  className="contact-pill"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon />
                  <span>{social.label}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>
    </section>
  );
}
